import { Table } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import {
  useDeleteEnergyLevel,
  useGetEnergyLevel,
} from 'api/Listings/EnergyLevel'
import Spinner from 'components/Spinner'
import moment from 'moment'
import { energyLevelStore } from 'state/listing/energyLevel'
import { EnergyLevelListing } from 'types/listing/listings'
import { PencilSimpleLine, Trash } from '@phosphor-icons/react'
import AlertModal from 'helper/AlertModal'

interface EnergyLevelProps {
  id: string
}

const EnergyLevel: React.FC<EnergyLevelProps> = ({ id }) => {
  const [alertModal, setAlertModal] = useState<boolean>(false)
  const { setEnergyLevelModal, setEnergyLevelData, energyLevelData } =
    energyLevelStore(store => ({
      setEnergyLevelModal: store.setEnergyLevelModal,
      setEnergyLevelData: store.setEnergyLevelData,
      energyLevelData: store.energyLevelData,
    }))
  const { data, isLoading, isFetching, refetch } = useGetEnergyLevel()
  const {
    mutate: deleteEnergyLevelMutation,
    isLoading: deleteEnergyLevelMutationLoading,
  } = useDeleteEnergyLevel()

  const handleEditClick = (data: EnergyLevelListing) => {
    setEnergyLevelData(data)
    setEnergyLevelModal(true)
  }

  const handleDeleteClick = (data: EnergyLevelListing) => {
    setEnergyLevelData(data)
    setAlertModal(true)
  }

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => <p className="text-14 w-400">{text}</p>,
    },
    {
      title: 'Icon',
      dataIndex: 'icon',
      key: 'icon',
      render: (text: string) => (
        <img src={text} alt="icon" className="w-30px h-30px radius-rounded" />
      ),
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (text: string) => (
        <p className="text-14 w-400">{moment(text).format('DD MMM, YYYY')}</p>
      ),
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      width: '100px',
      render: (data: any) => (
        <div className="flex align-center gap-20px">
          <PencilSimpleLine
            size={20}
            color="#000"
            className="pointer"
            onClick={() => handleEditClick(data)}
          />
          <Trash
            size={20}
            color="#ff6961"
            className="pointer"
            onClick={() => handleDeleteClick(data)}
          />
        </div>
      ),
    },
  ]

  const handleYesDelete = () => {
    deleteEnergyLevelMutation(energyLevelData._id, {
      onSuccess: () => {
        setEnergyLevelData(null)
        setAlertModal(false)
      },
    })
  }

  useEffect(() => {
    refetch()
  }, [id])

  const tableData = useMemo(() => {
    return (
      data?.map((item: EnergyLevelListing) => ({
        key: item._id,
        name: item.name,
        icon: item.icon.thumb,
        updatedAt: item.updatedAt,
        actions: item,
      })) || []
    )
  }, [data])

  return (
    <>
      <div>
        {isLoading || isFetching ? (
          <div className="w-full h-70vh flex align-center justify-center">
            <Spinner />
          </div>
        ) : (
          <Table
            className="colored_header_table"
            columns={columns}
            dataSource={tableData}
            pagination={false}
          />
        )}
      </div>
      {alertModal && (
        <AlertModal
          isModalVisible={alertModal}
          setIsModalVisible={setAlertModal}
          data={{
            name: 'Energy level',
            heading: 'Delete Energy level',
            message1: 'Are you sure you want to delete this level?',
            okText: 'Yes, delete',
          }}
          onYesClick={handleYesDelete}
          loading={deleteEnergyLevelMutationLoading}
        />
      )}
    </>
  )
}

export default EnergyLevel
